import React, { RefObject, useMemo, useRef } from 'react';
import Table from 'src/components/Table';
import { ITableColumn } from 'src/components/Table/types';
import { createSuiteTableColumns } from 'src/components/BulkOperations/Walkthroughs/BulkWalkthroughsSuiteTable/helpers';
import { UnitBulkOperationDto } from 'src/SharedCustomHooks/UsePropertySuites/types';
import './BulkWalkthroughSuiteTable.scss';

interface BulkWalkthroughsSuiteTableProps {
  suites: UnitBulkOperationDto[];
  selectedSuiteIds: string[];
  onSuiteSelectAll: (suiteId: string[]) => void;
  handleSuiteSelectIndividual: (suiteId: string) => void
  onDeselectAll: () => void;
  sendSchedulingEmail: boolean
}

const BulkWalkthroughsSuiteTable = ({
  suites,
  selectedSuiteIds,
  onSuiteSelectAll,
  handleSuiteSelectIndividual,
  onDeselectAll,
  sendSchedulingEmail
}: BulkWalkthroughsSuiteTableProps) => {
  const tableRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  const eligibleSuites: UnitBulkOperationDto[] = useMemo(() => {
    if (!sendSchedulingEmail) {
      return suites;
    }

    return suites.filter((suite: UnitBulkOperationDto) => suite.hasPrimaryContact);
  }, [suites, sendSchedulingEmail]);

  const handleSelectAll = () => {
    if (isAllEligibleSelected) {
      onDeselectAll();
    } else {
      const eligibleIds: string[] = eligibleSuites.map((suite: UnitBulkOperationDto) => suite.id);
      onSuiteSelectAll(eligibleIds);
    }
  };

  const isAllEligibleSelected: boolean = useMemo(() => {
    if (eligibleSuites.length === 0) {
      return false;
    }

    return eligibleSuites.every((suite: UnitBulkOperationDto) =>
      selectedSuiteIds.includes(suite.id)
    );
  }, [eligibleSuites, selectedSuiteIds]);

  const isAllSelected: boolean = isAllEligibleSelected;

  const columns: ITableColumn[] = useMemo(() => {
    return createSuiteTableColumns({
      allSelected: isAllSelected,
      onSelectAll: handleSelectAll,
      selectedSuiteIds,
      onSuiteSelect: handleSuiteSelectIndividual,
      sendSchedulingEmail
    });
  }, [isAllSelected, selectedSuiteIds, onSuiteSelectAll, sendSchedulingEmail]);

  return (
    <div className="suite-table">
      {suites.length === 0 ? (
        <div className="empty-state">
          <p>No suites available for this property.</p>
        </div>
      ) : (
        <div ref={tableRef}>
          <Table
            defaultSort={{
              accessor: 'number',
              direction: 'ascending',
            }}
            columns={columns}
            data={suites}
            headerTop={undefined}
          />
        </div>
      )}
    </div>
  );
};

export default BulkWalkthroughsSuiteTable;
